import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back to CreativeHub");
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#E5E7EB] grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-white border-r border-gray-200">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-[#002FA7] flex items-center justify-center text-white">
            <Sparkles className="w-5 h-5" strokeWidth={1.6} />
          </div>
          <span className="font-display font-bold text-lg tracking-tight">CreativeHub Website Pro Builder</span>
        </div>
        <div className="space-y-6">
          <h1 className="font-display text-5xl font-bold tracking-tighter leading-[1.05]">
            Design.<br />Publish.<br /><span className="text-[#002FA7]">Ship Faster.</span>
          </h1>
          <p className="text-base text-gray-600 max-w-md leading-relaxed">
            A Canva-style website builder built for shipping. Stack vertical pages, drag, drop, publish — no clutter, no friction.
          </p>
        </div>
        <p className="text-xs text-gray-400 font-mono">© 2026 CreativeHub. PayFast secured billing.</p>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <form onSubmit={submit} className="w-full max-w-md space-y-6 ch-fade" data-testid="login-form">
          <div className="space-y-2">
            <h2 className="font-display text-3xl font-bold tracking-tight">Sign in</h2>
            <p className="text-sm text-gray-500">Welcome back. Use your CreativeHub credentials.</p>
          </div>
          <div className="space-y-3">
            <Input
              type="email"
              placeholder="you@studio.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email-input"
              required
              autoFocus
            />
            <Input
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password-input"
              required
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="login-submit-button"
            className="w-full bg-[#002FA7] hover:bg-blue-800 text-white h-11"
          >
            {loading ? "Signing in…" : "Sign in"}
          </Button>
          <p className="text-sm text-gray-500 text-center">
            New here?{" "}
            <Link to="/register" className="text-[#002FA7] font-medium hover:underline" data-testid="login-register-link">
              Create an account
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
